import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Settings, Mail, Database, Loader2, Save, Download, Upload, RefreshCw, CheckCircle, Clock, Shield, KeyRound, MessageSquare, AlertCircle, Trash2, Eye, EyeOff, ChevronLeft, ChevronRight, ChevronDown, ChevronUp, X, Send, MapPin, FileBarChart } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';

import { API } from '../utils/api';
import { APP_VERSION } from '../utils/version';

function fmtMoney(v) {
  const n = Number(v) || 0;
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function FsSectionRows({ items, ytdItems, emptyLabel }) {
  const names = Array.from(new Set([...Object.keys(items || {}), ...Object.keys(ytdItems || {})])).sort();
  if (names.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={3} className="text-slate-400 italic">{emptyLabel}</TableCell>
      </TableRow>
    );
  }
  return (
    <>
      {names.map((name) => (
        <TableRow key={name}>
          <TableCell className="py-1">{name}</TableCell>
          <TableCell className="py-1 text-right">{fmtMoney((items || {})[name] || 0)}</TableCell>
          <TableCell className="py-1 text-right text-slate-500">{fmtMoney((ytdItems || {})[name] || 0)}</TableCell>
        </TableRow>
      ))}
    </>
  );
}

function FsBsRows({ items, emptyLabel }) {
  const entries = Object.entries(items || {});
  if (entries.length === 0) {
    return (
      <TableRow>
        <TableCell colSpan={2} className="text-slate-400 italic">{emptyLabel}</TableCell>
      </TableRow>
    );
  }
  entries.sort((a, b) => a[0].localeCompare(b[0]));
  return (
    <>
      {entries.map(([name, amt]) => (
        <TableRow key={name}>
          <TableCell className="py-1">{name}</TableCell>
          <TableCell className="py-1 text-right">{fmtMoney(amt)}</TableCell>
        </TableRow>
      ))}
    </>
  );
}

function FsIncomeStatement({ llc, periodStart, periodEnd }) {
  const ytdNet = Number(llc.ytd_net_income) || 0;
  const net = Number(llc.net_income) || 0;
  return (
    <div className="mt-3">
      <div className="text-sm font-semibold text-slate-900 mb-1">
        Income Statement <span className="font-normal text-slate-500">({periodStart} – {periodEnd})</span>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="text-left">Account</TableHead>
            <TableHead className="text-center">Quarter</TableHead>
            <TableHead className="text-center">YTD</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow className="bg-slate-100">
            <TableCell colSpan={3} className="font-semibold py-1">Income</TableCell>
          </TableRow>
          <FsSectionRows items={llc.income} ytdItems={llc.ytd_income} emptyLabel="No income" />
          <TableRow>
            <TableCell className="font-semibold py-1">Total Income</TableCell>
            <TableCell className="font-semibold py-1 text-right">{fmtMoney(llc.total_income)}</TableCell>
            <TableCell className="font-semibold py-1 text-right text-slate-600">{fmtMoney(llc.ytd_total_income)}</TableCell>
          </TableRow>
          <TableRow className="bg-slate-100">
            <TableCell colSpan={3} className="font-semibold py-1">Expenses</TableCell>
          </TableRow>
          <FsSectionRows items={llc.expenses} ytdItems={llc.ytd_expenses} emptyLabel="No expenses" />
          <TableRow>
            <TableCell className="font-semibold py-1">Total Expenses</TableCell>
            <TableCell className="font-semibold py-1 text-right">{fmtMoney(llc.total_expenses)}</TableCell>
            <TableCell className="font-semibold py-1 text-right text-slate-600">{fmtMoney(llc.ytd_total_expenses)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="font-bold py-2 border-t-2 border-slate-900">Net Income</TableCell>
            <TableCell className={`font-bold py-2 text-right border-t-2 border-slate-900 ${net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {fmtMoney(net)}
            </TableCell>
            <TableCell className={`font-bold py-2 text-right border-t-2 border-slate-900 ${ytdNet >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {fmtMoney(ytdNet)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <div className="text-xs text-slate-400 mt-1">
        YTD covers {llc.ytd_start || ''} – {periodEnd}
      </div>
    </div>
  );
}

function FsBalanceSheet({ bs }) {
  if (!bs) return null;
  return (
    <div className="mt-4">
      <div className="text-sm font-semibold text-slate-900 mb-1">
        Balance Sheet <span className="font-normal text-slate-500">(as of {bs.as_of_date})</span>
      </div>
      <Table>
        <TableBody>
          <TableRow className="bg-slate-100">
            <TableCell colSpan={2} className="font-semibold py-1">Assets</TableCell>
          </TableRow>
          <FsBsRows items={bs.current_assets} emptyLabel="No current assets" />
          <TableRow>
            <TableCell className="italic text-slate-600 py-1">Total Current Assets</TableCell>
            <TableCell className="italic text-slate-600 py-1 text-right">{fmtMoney(bs.total_current_assets)}</TableCell>
          </TableRow>
          {Object.keys(bs.fixed_assets || {}).length > 0 && (
            <>
              <FsBsRows items={bs.fixed_assets} emptyLabel="" />
              <TableRow>
                <TableCell className="italic text-slate-600 py-1">Total Fixed Assets</TableCell>
                <TableCell className="italic text-slate-600 py-1 text-right">{fmtMoney(bs.total_fixed_assets)}</TableCell>
              </TableRow>
            </>
          )}
          {Object.keys(bs.other_assets || {}).length > 0 && (
            <FsBsRows items={bs.other_assets} emptyLabel="" />
          )}
          <TableRow>
            <TableCell className="font-bold py-2 border-t-2 border-slate-900">Total Assets</TableCell>
            <TableCell className="font-bold py-2 text-right border-t-2 border-slate-900">{fmtMoney(bs.total_assets)}</TableCell>
          </TableRow>
          <TableRow className="bg-slate-100">
            <TableCell colSpan={2} className="font-semibold py-1">Liabilities</TableCell>
          </TableRow>
          <FsBsRows items={bs.current_liabilities} emptyLabel="No current liabilities" />
          <TableRow>
            <TableCell className="italic text-slate-600 py-1">Total Current Liabilities</TableCell>
            <TableCell className="italic text-slate-600 py-1 text-right">{fmtMoney(bs.total_current_liabilities)}</TableCell>
          </TableRow>
          {Object.keys(bs.long_term_liabilities || {}).length > 0 && (
            <>
              <FsBsRows items={bs.long_term_liabilities} emptyLabel="" />
              <TableRow>
                <TableCell className="italic text-slate-600 py-1">Total Long-term Liabilities</TableCell>
                <TableCell className="italic text-slate-600 py-1 text-right">{fmtMoney(bs.total_long_term_liabilities)}</TableCell>
              </TableRow>
            </>
          )}
          <TableRow>
            <TableCell className="font-semibold py-1">Total Liabilities</TableCell>
            <TableCell className="font-semibold py-1 text-right">{fmtMoney(bs.total_liabilities)}</TableCell>
          </TableRow>
          <TableRow className="bg-slate-100">
            <TableCell colSpan={2} className="font-semibold py-1">Equity</TableCell>
          </TableRow>
          <FsBsRows items={bs.equity} emptyLabel="No equity" />
          <TableRow>
            <TableCell className="font-semibold py-1">Total Equity</TableCell>
            <TableCell className="font-semibold py-1 text-right">{fmtMoney(bs.total_equity)}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell className="font-bold py-2 border-t-2 border-slate-900">Total Liabilities &amp; Equity</TableCell>
            <TableCell className="font-bold py-2 text-right border-t-2 border-slate-900">{fmtMoney(bs.total_liabilities_and_equity)}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

function FsLLCReport({ llc, periodStart, periodEnd }) {
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden mt-4">
      <div className="bg-slate-900 text-white px-4 py-3">
        <div className="text-base font-semibold">{llc.llc_name}</div>
        <div className="text-xs opacity-80">Ownership Interest: {(Number(llc.ownership_percentage) || 0).toFixed(2)}%</div>
      </div>
      <div className="px-4 pb-4">
        <FsIncomeStatement llc={llc} periodStart={periodStart} periodEnd={periodEnd} />
        <FsBalanceSheet bs={llc.balance_sheet} />
      </div>
    </div>
  );
}

function FsRecipientCard({ r }) {
  const llcs = r.llcs || [];
  return (
    <div className="border border-slate-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="font-medium text-base">{r.investor_name}</div>
          <div className="text-xs text-slate-500">{r.investor_email}</div>
        </div>
        <Badge variant="outline">{r.llc_count} LLC(s)</Badge>
      </div>
      {llcs.map((l) => (
        <FsLLCReport key={l.llc_id} llc={l} periodStart={r.period_start} periodEnd={r.period_end} />
      ))}
    </div>
  );
}

function FsDraftBanner({ appName }) {
  return (
    <div className="rounded-md border border-orange-300 bg-orange-50 border-l-4 border-l-orange-600 px-4 py-3 mb-4">
      <div className="font-bold text-orange-900 tracking-wide">DRAFT REPORT</div>
      <div className="text-sm text-orange-900 mt-1">
        This is a <strong>draft</strong> quarterly statement automatically generated and sent by the {appName || 'GCBS Management'} system.
        Figures are unaudited and may be revised. Please review and contact your manager with any questions before relying on these numbers.
      </div>
    </div>
  );
}

function renderFsPreviewBody(fsPreview) {
  if (!fsPreview) return null;
  const recipients = fsPreview.recipients || [];
  if (recipients.length === 0) {
    return (
      <p className="text-sm text-slate-600 py-4">
        No eligible recipients. Either no investors with email addresses own LLCs that had activity in this quarter,
        or your filters excluded everyone.
      </p>
    );
  }
  return (
    <div className="space-y-4">
      <FsDraftBanner appName={fsPreview.app_name} />
      {recipients.map((r) => <FsRecipientCard key={r.investor_id} r={r} />)}
    </div>
  );
}

function BackupStatusPill({ settings }) {
  const { last_sent_at, last_status, enabled } = settings || {};
  if (!last_sent_at) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200" data-testid="backup-status-pill">
        <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
        {enabled ? 'Scheduled · Never run' : 'Not configured'}
      </span>
    );
  }
  let when = last_sent_at;
  try {
    const d = new Date(last_sent_at);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMin / 60);
    const diffDays = Math.floor(diffHrs / 24);
    if (diffMin < 1) when = 'just now';
    else if (diffMin < 60) when = `${diffMin}m ago`;
    else if (diffHrs < 24) when = `${diffHrs}h ago`;
    else when = `${diffDays}d ago`;
  } catch (e) {
    console.warn('Setup: failed to format backup timestamp', e);
    /* keep raw */
  }
  if (last_status === 'sent') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="backup-status-pill">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        Last backup: {when}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200" data-testid="backup-status-pill">
      <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
      Backup failed: {when}
    </span>
  );
}

function Setup() {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [savingTimeout, setSavingTimeout] = useState(false);
  const [emailLogs, setEmailLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [digestSettings, setDigestSettings] = useState({
    digest_enabled: false,
    digest_day: 'monday',
    digest_time: '09:00'
  });
  const [savingDigest, setSavingDigest] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previews, setPreviews] = useState([]);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [settings, setSettings] = useState({
    app_name: 'Asset Commander',
    app_byline: 'LLC Management',
    footer_text: 'Asset Commander',
    footer_copyright: 'Global LLC Management',
    app_version: '1.0.0',
    app_url: '',
    smtp_host: '',
    smtp_port: 587,
    smtp_user: '',
    smtp_password: '',
    smtp_from_email: '',
    smtp_from_name: '',
    smtp_use_tls: true,
    session_timeout_minutes: 25,
    password_reset_days: 90,
    password_expiry_enabled: false,
    master_company_name: '',
    master_address_line1: '',
    master_address_line2: '',
    master_city: '',
    master_state: '',
    master_zip: '',
    master_phone: '',
    master_ein: ''
  });
  const [backupData, setBackupData] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [backupEmailSettings, setBackupEmailSettings] = useState({ enabled: false, frequency: 'weekly', day_of_week: 'monday', day_of_month: 1, time: '06:00', email: '', last_sent_at: null, last_status: null, last_error: '' });
  const [savingBackupEmail, setSavingBackupEmail] = useState(false);
  const [sendingBackupNow, setSendingBackupNow] = useState(false);
  const [savingPasswordPolicy, setSavingPasswordPolicy] = useState(false);

  // Weekly Task Digest panel collapse state (default collapsed)
  const [digestCollapsed, setDigestCollapsed] = useState(true);

  // Financial Statements (Quarterly) emailer state
  const _now = new Date();
  const _currentQuarter = Math.floor(_now.getMonth() / 3) + 1;
  const _defaultQuarter = _currentQuarter === 1 ? 4 : _currentQuarter - 1;
  const _defaultYear = _currentQuarter === 1 ? _now.getFullYear() - 1 : _now.getFullYear();
  const [fsCollapsed, setFsCollapsed] = useState(true);
  const [fsYear, setFsYear] = useState(_defaultYear);
  const [fsQuarter, setFsQuarter] = useState(_defaultQuarter);
  const [fsAllLLCs, setFsAllLLCs] = useState(true);
  const [fsAllInvestors, setFsAllInvestors] = useState(true);
  const [fsSelectedLLCs, setFsSelectedLLCs] = useState([]); // ids
  const [fsSelectedInvestors, setFsSelectedInvestors] = useState([]); // ids
  const [fsLLCs, setFsLLCs] = useState([]);
  const [fsInvestors, setFsInvestors] = useState([]);
  const [fsLoadingPreview, setFsLoadingPreview] = useState(false);
  const [fsSending, setFsSending] = useState(false);
  const [fsPreview, setFsPreview] = useState(null);
  const [fsPreviewOpen, setFsPreviewOpen] = useState(false);

  useEffect(() => {
    fetchSettings();
    fetchEmailLogs();
    fetchDigestSettings();
    fetchBackupEmailSettings();
  }, []);

  const fetchDigestSettings = () => {
    axios.get(API + '/digest/settings', { headers: getAuthHeaders() })
      .then(res => {
        setDigestSettings(res.data);
      })
      .catch(() => {});
  };

  const handleSaveDigestSettings = () => {
    setSavingDigest(true);
    axios.post(API + '/digest/settings', digestSettings, { headers: getAuthHeaders() })
      .then(() => {
        toast.success('Digest settings saved');
        setSavingDigest(false);
      })
      .catch(err => {
        toast.error(err.response?.data?.detail || 'Failed to save digest settings');
        setSavingDigest(false);
      });
  };

  const handleSendDigestNow = () => {
    setSendingDigest(true);
    axios.post(API + '/digest/send-weekly', {}, { headers: getAuthHeaders() })
      .then(res => {
        if (res.data.sent_to && res.data.sent_to.length > 0) {
          const recipients = res.data.sent_to.map(u => `${u.name} (${u.task_count} tasks)`).join(', ');
          toast.success(`Digest sent to: ${recipients}`);
        } else {
          toast.success(res.data.message);
        }
        setSendingDigest(false);
        fetchEmailLogs();
      })
      .catch(err => {
        toast.error(err.response?.data?.detail || 'Failed to send digest');
        setSendingDigest(false);
        fetchEmailLogs();
      });
  };

  const handlePreviewDigest = () => {
    setPreviewLoading(true);
    setPreviewOpen(true);
    axios.get(API + '/digest/preview', { headers: getAuthHeaders() })
      .then(res => {
        setPreviews(res.data.previews || []);
        setCurrentPreviewIndex(0);
        setPreviewLoading(false);
        if (res.data.previews?.length === 0) {
          toast.info(res.data.message || 'No users are eligible for digest');
        }
      })
      .catch(err => {
        toast.error(err.response?.data?.detail || 'Failed to load preview');
        setPreviewLoading(false);
        setPreviewOpen(false);
      });
  };

  const fetchEmailLogs = () => {
    setLoadingLogs(true);
    axios.get(API + '/email-logs?limit=100', { headers: getAuthHeaders() })
      .then(res => {
        setEmailLogs(res.data.logs || []);
        setLoadingLogs(false);
      })
      .catch(() => {
        setLoadingLogs(false);
      });
  };

  // Lazy-load LLCs and Investors when the Financial Statements panel opens
  const fetchFsLLCs = () => {
    if (fsLLCs.length) return;
    axios.get(API + '/llcs', { headers: getAuthHeaders() })
      .then(res => setFsLLCs(res.data || []))
      .catch(() => {});
  };
  const fetchFsInvestors = () => {
    if (fsInvestors.length) return;
    axios.get(API + '/investors', { headers: getAuthHeaders() })
      .then(res => setFsInvestors(res.data || []))
      .catch(() => {});
  };

  const toggleFsLLC = (id) => {
    setFsSelectedLLCs(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleFsInvestor = (id) => {
    setFsSelectedInvestors(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const buildFsPayload = () => ({
    year: Number(fsYear),
    quarter: Number(fsQuarter),
    llc_ids: fsAllLLCs ? null : fsSelectedLLCs,
    investor_ids: fsAllInvestors ? null : fsSelectedInvestors,
  });

  const handlePreviewFinancialStatements = () => {
    setFsLoadingPreview(true);
    axios.post(API + '/financial-statements/preview-quarterly', buildFsPayload(), { headers: getAuthHeaders() })
      .then(res => {
        setFsPreview(res.data);
        setFsPreviewOpen(true);
      })
      .catch(err => {
        toast.error(err.response?.data?.detail || 'Failed to preview quarterly recipients');
      })
      .finally(() => setFsLoadingPreview(false));
  };

  const handleSendFinancialStatements = () => {
    if (!window.confirm(`Send Q${fsQuarter} ${fsYear} financial statements via email?`)) return;
    setFsSending(true);
    axios.post(API + '/financial-statements/send-quarterly', buildFsPayload(), { headers: getAuthHeaders() })
      .then(res => {
        const sentCount = res.data.sent_to?.length || 0;
        const failedCount = res.data.failed?.length || 0;
        if (sentCount > 0) toast.success(`Quarterly statements sent to ${sentCount} investor(s)${failedCount ? `, ${failedCount} failed` : ''}`);
        else toast.warning(res.data.message || 'No emails were sent');
        fetchEmailLogs();
      })
      .catch(err => {
        toast.error(err.response?.data?.detail || 'Failed to send quarterly statements');
      })
      .finally(() => setFsSending(false));
  };

  const clearEmailLogs = () => {
    if (!window.confirm('Are you sure you want to clear all email logs?')) return;
    axios.delete(API + '/email-logs', { headers: getAuthHeaders() })
      .then(res => {
        toast.success(res.data.message);
        setEmailLogs([]);
      })
      .catch(err => {
        toast.error(err.response?.data?.detail || 'Failed to clear logs');
      });
  };

  const openEmailPreview = (logId) => {
    setLoadingPreview(true);
    setEmailPreview(null);
    axios.get(`${API}/email-logs/${logId}`, { headers: getAuthHeaders() })
      .then(res => {
        setEmailPreview(res.data);
      })
      .catch(() => {
        toast.error('Could not load email preview');
      })
      .finally(() => setLoadingPreview(false));
  };

  const fetchSettings = () => {
    axios.get(API + '/settings', { headers: getAuthHeaders() })
      .then(res => {
        setSettings(prev => ({ ...prev, ...res.data }));
        setLoading(false);
      })
      .catch(() => {
        toast.error('Failed to load settings');
        setLoading(false);
      });
  };

  const handleSaveFooter = () => {
    setSaving(true);
    axios.put(API + '/settings', {
      app_name: settings.app_name,
      app_byline: settings.app_byline,
      footer_text: settings.footer_text,
      footer_copyright: settings.footer_copyright,
      app_version: settings.app_version,
      app_url: settings.app_url
    }, { headers: getAuthHeaders() })
      .then(() => {
        toast.success('Settings saved. Refresh the page to see changes in header/footer.');
        setSaving(false);
      })
      .catch(err => {
        toast.error(err.response?.data?.detail || 'Failed to save');
        setSaving(false);
      });
  };

  const handleSaveSMTP = () => {
    setSaving(true);
    axios.put(API + '/settings', {
      smtp_host: settings.smtp_host,
      smtp_port: settings.smtp_port,
      smtp_user: settings.smtp_user,
      smtp_password: settings.smtp_password,
      smtp_from_email: settings.smtp_from_email,
      smtp_from_name: settings.smtp_from_name,
      smtp_use_tls: settings.smtp_use_tls
    }, { headers: getAuthHeaders() })
      .then(() => {
        toast.success('SMTP settings saved');
        setSaving(false);
      })
      .catch(err => {
        toast.error(err.response?.data?.detail || 'Failed to save');
        setSaving(false);
      });
  };

  const handleSaveTimeout = () => {
    setSavingTimeout(true);
    axios.put(API + '/settings', {
      session_timeout_minutes: settings.session_timeout_minutes
    }, { headers: getAuthHeaders() })
      .then(() => {
        toast.success('Session timeout saved. Log out and back in for changes to take effect.');
        setSavingTimeout(false);
      })
      .catch(err => {
        toast.error(err.response?.data?.detail || 'Failed to save');
        setSavingTimeout(false);
      });
  };

  const handleSavePasswordPolicy = () => {
    setSavingPasswordPolicy(true);
    axios.put(API + '/settings', {
      password_reset_days: settings.password_reset_days,
      password_expiry_enabled: !!settings.password_expiry_enabled
    }, { headers: getAuthHeaders() })
      .then(() => {
        toast.success('Password policy saved successfully.');
        setSavingPasswordPolicy(false);
      })
      .catch(err => {
        toast.error(err.response?.data?.detail || 'Failed to save');
        setSavingPasswordPolicy(false);
      });
  };

  const [savingAddress, setSavingAddress] = useState(false);
  const handleSaveAddress = () => {
    setSavingAddress(true);
    axios.put(API + '/settings', {
      master_company_name: settings.master_company_name,
      master_address_line1: settings.master_address_line1,
      master_address_line2: settings.master_address_line2,
      master_city: settings.master_city,
      master_state: settings.master_state,
      master_zip: settings.master_zip,
      master_phone: settings.master_phone,
      master_ein: settings.master_ein
    }, { headers: getAuthHeaders() })
      .then(() => {
        toast.success('Master address saved. This will be used on all 1099s and communications.');
        setSavingAddress(false);
      })
      .catch(err => {
        toast.error(err.response?.data?.detail || 'Failed to save');
        setSavingAddress(false);
      });
  };

  const handleTestSMTP = () => {
    setSaving(true);
    const body = testEmailAddress.trim() ? { email: testEmailAddress.trim() } : {};
    axios.post(API + '/settings/test-smtp', body, { headers: getAuthHeaders() })
      .then(res => {
        if (res.data.sent_to && res.data.sent_to.length > 0) {
          const recipients = res.data.sent_to.map(u => u.email).join(', ');
          toast.success(`Test email sent to: ${recipients}`);
        } else {
          toast.success(res.data.message);
        }
        setSaving(false);
        fetchEmailLogs(); // Refresh logs after sending
      })
      .catch(err => {
        toast.error(err.response?.data?.detail || 'SMTP test failed');
        setSaving(false);
        fetchEmailLogs(); // Refresh logs to show failed attempt
      });
  };

  const handleCreateBackup = () => {
    axios.get(API + '/backup', { headers: getAuthHeaders() })
      .then(res => {
        const dataStr = JSON.stringify(res.data, null, 2);
        const blob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `asset-commander-backup-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success('Backup downloaded');
      })
      .catch(err => {
        toast.error(err.response?.data?.detail || 'Failed to create backup');
      });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = JSON.parse(event.target?.result);
        setBackupData(data);
        toast.success('Backup file loaded. Click Restore to apply.');
      } catch (err) {
        toast.error('Invalid backup file');
      }
    };
    reader.readAsText(file);
  };

  const handleRestore = () => {
    if (!backupData) {
      toast.error('No backup file loaded');
      return;
    }

    setRestoring(true);
    axios.post(API + '/restore', backupData, { headers: getAuthHeaders() })
      .then(res => {
        toast.success(res.data.message);
        setBackupData(null);
        setRestoring(false);
      })
      .catch(err => {
        toast.error(err.response?.data?.detail || 'Restore failed');
        setRestoring(false);
      });
  };

  const fetchBackupEmailSettings = () => {
    axios.get(API + '/settings/backup-email', { headers: getAuthHeaders() })
      .then(res => setBackupEmailSettings(res.data))
      .catch(() => {});
  };

  const saveBackupEmailSettings = () => {
    setSavingBackupEmail(true);
    axios.post(API + '/settings/backup-email', backupEmailSettings, { headers: getAuthHeaders() })
      .then(res => {
        toast.success(res.data.message);
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to save'))
      .finally(() => setSavingBackupEmail(false));
  };

  const sendBackupNow = () => {
    if (!backupEmailSettings.email) {
      toast.error('Please enter an email address first');
      return;
    }
    setSendingBackupNow(true);
    // Save settings first, then send
    axios.post(API + '/settings/backup-email', backupEmailSettings, { headers: getAuthHeaders() })
      .then(() => axios.post(API + '/backup/send-email', { email: backupEmailSettings.email }, { headers: getAuthHeaders() }))
      .then(res => {
        toast.success(res.data.message);
        fetchEmailLogs();
      })
      .catch(err => toast.error(err.response?.data?.detail || 'Failed to send backup email'))
      .finally(() => setSendingBackupNow(false));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="setup-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-serif font-semibold text-slate-900">Setup</h1>
        <p className="text-slate-500 mt-1">Configure system settings</p>
      </div>

      <Tabs defaultValue="footer" className="w-full">
        <TabsList className="grid w-full grid-cols-6 lg:w-[780px]">
          <TabsTrigger value="footer" data-testid="tab-footer">
            <Settings className="w-4 h-4 mr-2" />
            Footer
          </TabsTrigger>
          <TabsTrigger value="address" data-testid="tab-address">
            <MapPin className="w-4 h-4 mr-2" />
            Address
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security">
            <Shield className="w-4 h-4 mr-2" />
            Security
          </TabsTrigger>
          <TabsTrigger value="smtp" data-testid="tab-smtp">
            <Mail className="w-4 h-4 mr-2" />
            SMTP
          </TabsTrigger>
          <TabsTrigger value="communications" data-testid="tab-communications">
            <MessageSquare className="w-4 h-4 mr-2" />
            Communications
          </TabsTrigger>
          <TabsTrigger value="backup" data-testid="tab-backup">
            <Database className="w-4 h-4 mr-2" />
            Backup
          </TabsTrigger>
        </TabsList>

        {/* Footer & Version Tab */}
        <TabsContent value="footer" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-serif">App Name & Branding</CardTitle>
              <CardDescription>Customize the application name, byline, footer and version</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* App Name Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Header</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="app_name">App Name</Label>
                    <Input
                      id="app_name"
                      value={settings.app_name}
                      onChange={(e) => setSettings({ ...settings, app_name: e.target.value })}
                      placeholder="Asset Commander"
                    />
                    <p className="text-xs text-slate-500">Displayed in the header navigation</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="app_byline">App Byline</Label>
                    <Input
                      id="app_byline"
                      value={settings.app_byline}
                      onChange={(e) => setSettings({ ...settings, app_byline: e.target.value })}
                      placeholder="LLC Management"
                    />
                    <p className="text-xs text-slate-500">Subtitle shown below app name</p>
                  </div>
                </div>
              </div>

              <hr className="border-slate-200" />

              {/* Footer Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Footer</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="footer_text">Footer Title</Label>
                    <Input
                      id="footer_text"
                      value={settings.footer_text}
                      onChange={(e) => setSettings({ ...settings, footer_text: e.target.value })}
                      placeholder="Asset Commander"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="footer_copyright">Footer Subtitle</Label>
                    <Input
                      id="footer_copyright"
                      value={settings.footer_copyright}
                      onChange={(e) => setSettings({ ...settings, footer_copyright: e.target.value })}
                      placeholder="Global LLC Management"
                    />
                  </div>
                </div>
              </div>

              <hr className="border-slate-200" />

              {/* Version Section */}
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-slate-700 uppercase tracking-wide">Version</h3>
                <div className="space-y-2">
                  <Label>Application Version</Label>
                  <div className="flex items-center gap-2">
                    <div className="px-3 py-2 rounded-md border border-slate-200 bg-slate-50 font-mono text-sm text-slate-700 max-w-xs" data-testid="app-version-auto">
                      {APP_VERSION}
                    </div>
                    <span className="text-xs text-slate-500">Auto-generated from build</span>
                  </div>
                  <p className="text-xs text-slate-500">Displayed in the footer. Bump <code className="text-[11px] bg-slate-100 px-1 rounded">frontend/package.json</code> to change.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="app_url">Application URL</Label>
                  <Input
                    id="app_url"
                    value={settings.app_url}
                    onChange={(e) => setSettings({ ...settings, app_url: e.target.value })}
                    placeholder="https://your-app-domain.com"
                  />
                  <p className="text-xs text-slate-500">Used in email links (e.g., "Log In for More Details")</p>
                </div>
              </div>

              <div className="pt-4">
                <Button onClick={handleSaveFooter} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Branding Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Address Tab */}
        <TabsContent value="address" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-serif">Master Mailing Address</CardTitle>
              <p className="text-sm text-slate-500">This address will be used as the payer address on all 1099 forms and communications instead of individual LLC property addresses.</p>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Company Name</Label>
                <Input
                  value={settings.master_company_name}
                  onChange={(e) => setSettings({ ...settings, master_company_name: e.target.value })}
                  placeholder="GCBS Management"
                  data-testid="master-company-name"
                />
              </div>
              <Separator />
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Address</Label>
                <Input
                  value={settings.master_address_line1}
                  onChange={(e) => setSettings({ ...settings, master_address_line1: e.target.value })}
                  placeholder="Street address"
                  data-testid="master-address-line1"
                />
                <Input
                  value={settings.master_address_line2}
                  onChange={(e) => setSettings({ ...settings, master_address_line2: e.target.value })}
                  placeholder="Suite, unit, building (optional)"
                  data-testid="master-address-line2"
                />
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">City</Label>
                    <Input
                      value={settings.master_city}
                      onChange={(e) => setSettings({ ...settings, master_city: e.target.value })}
                      placeholder="City"
                      data-testid="master-city"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">State</Label>
                    <Input
                      value={settings.master_state}
                      onChange={(e) => setSettings({ ...settings, master_state: e.target.value })}
                      placeholder="TX"
                      data-testid="master-state"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">ZIP</Label>
                    <Input
                      value={settings.master_zip}
                      onChange={(e) => setSettings({ ...settings, master_zip: e.target.value })}
                      placeholder="77001"
                      data-testid="master-zip"
                    />
                  </div>
                </div>
              </div>
              <Separator />
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Phone</Label>
                  <Input
                    value={settings.master_phone}
                    onChange={(e) => setSettings({ ...settings, master_phone: e.target.value })}
                    placeholder="(713) 555-0100"
                    data-testid="master-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">EIN</Label>
                  <Input
                    value={settings.master_ein}
                    onChange={(e) => setSettings({ ...settings, master_ein: e.target.value })}
                    placeholder="XX-XXXXXXX"
                    data-testid="master-ein"
                  />
                  <p className="text-xs text-slate-400">Used as Payer's TIN on 1099 forms</p>
                </div>
              </div>
              <Button onClick={handleSaveAddress} disabled={savingAddress} data-testid="save-address-btn">
                {savingAddress ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Master Address
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Tab */}
        <TabsContent value="security" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-serif flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Session Timeout
              </CardTitle>
              <CardDescription>Configure automatic logout after period of inactivity</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="session_timeout">Inactivity Timeout (minutes)</Label>
                  <div className="flex items-center gap-4">
                    <Input
                      id="session_timeout"
                      type="number"
                      min="1"
                      max="120"
                      value={settings.session_timeout_minutes}
                      onChange={(e) => setSettings({ ...settings, session_timeout_minutes: parseInt(e.target.value) || 25 })}
                      className="max-w-[120px]"
                    />
                    <span className="text-sm text-slate-500">minutes</span>
                  </div>
                  <p className="text-xs text-slate-500">
                    Users will be automatically logged out after this period of inactivity. A warning will appear 1 minute before logout.
                  </p>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2">
                  <h4 className="font-medium text-slate-700">Quick Presets</h4>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSettings({ ...settings, session_timeout_minutes: 1 })}
                      className={settings.session_timeout_minutes === 1 ? 'bg-amber-100 border-amber-400' : ''}
                    >
                      1 min (Testing)
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSettings({ ...settings, session_timeout_minutes: 5 })}
                      className={settings.session_timeout_minutes === 5 ? 'bg-amber-100 border-amber-400' : ''}
                    >
                      5 min
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSettings({ ...settings, session_timeout_minutes: 15 })}
                      className={settings.session_timeout_minutes === 15 ? 'bg-amber-100 border-amber-400' : ''}
                    >
                      15 min
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSettings({ ...settings, session_timeout_minutes: 25 })}
                      className={settings.session_timeout_minutes === 25 ? 'bg-amber-100 border-amber-400' : ''}
                    >
                      25 min (Default)
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSettings({ ...settings, session_timeout_minutes: 45 })}
                      className={settings.session_timeout_minutes === 45 ? 'bg-amber-100 border-amber-400' : ''}
                    >
                      45 min
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSettings({ ...settings, session_timeout_minutes: 60 })}
                      className={settings.session_timeout_minutes === 60 ? 'bg-amber-100 border-amber-400' : ''}
                    >
                      60 min
                    </Button>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <p className="text-sm text-amber-800">
                    <strong>Note:</strong> After saving, you'll need to log out and log back in for the new timeout setting to take effect.
                  </p>
                </div>
              </div>

              <div className="pt-4">
                <Button onClick={handleSaveTimeout} disabled={savingTimeout} className="bg-slate-900 hover:bg-slate-800">
                  {savingTimeout ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Session Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Password Reset Policy Card */}
          <Card className="border-slate-200 mt-6">
            <CardHeader>
              <CardTitle className="font-serif flex items-center gap-2">
                <KeyRound className="w-5 h-5" />
                Password Reset Policy
              </CardTitle>
              <CardDescription>Optionally require users to change their password periodically</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between gap-4 bg-slate-50 border border-slate-200 rounded-lg p-4">
                <div>
                  <Label htmlFor="password_expiry_enabled" className="font-medium">Require periodic password changes</Label>
                  <p className="text-xs text-slate-500 mt-1">
                    Off by default. Sign-ins are already protected by emailed verification codes, so users only change their password when they want to.
                  </p>
                </div>
                <Switch
                  id="password_expiry_enabled"
                  checked={!!settings.password_expiry_enabled}
                  onCheckedChange={(checked) => setSettings({ ...settings, password_expiry_enabled: checked })}
                  data-testid="password-expiry-toggle"
                />
              </div>
              {settings.password_expiry_enabled && (
              <div className="space-y-4" data-testid="password-expiry-options">
                <div className="space-y-2">
                  <Label htmlFor="password_reset_days">Password Expiration (days)</Label>
                  <div className="flex items-center gap-4">
                    <Input
                      id="password_reset_days"
                      type="number"
                      min="30"
                      max="365"
                      value={settings.password_reset_days}
                      onChange={(e) => setSettings({ ...settings, password_reset_days: parseInt(e.target.value) || 90 })}
                      className="max-w-[120px]"
                    />
                    <span className="text-sm text-slate-500">days</span>
                  </div>
                  <p className="text-xs text-slate-500">
                    Users will be required to change their password after this many days. Warnings appear at 5 days and 3 days before expiration.
                  </p>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 space-y-2">
                  <h4 className="font-medium text-slate-700">Quick Presets</h4>
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSettings({ ...settings, password_reset_days: 30 })}
                      className={settings.password_reset_days === 30 ? 'bg-amber-100 border-amber-400' : ''}
                    >
                      30 days
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSettings({ ...settings, password_reset_days: 60 })}
                      className={settings.password_reset_days === 60 ? 'bg-amber-100 border-amber-400' : ''}
                    >
                      60 days
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSettings({ ...settings, password_reset_days: 90 })}
                      className={settings.password_reset_days === 90 ? 'bg-amber-100 border-amber-400' : ''}
                    >
                      90 days (Default)
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSettings({ ...settings, password_reset_days: 180 })}
                      className={settings.password_reset_days === 180 ? 'bg-amber-100 border-amber-400' : ''}
                    >
                      180 days
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSettings({ ...settings, password_reset_days: 365 })}
                      className={settings.password_reset_days === 365 ? 'bg-amber-100 border-amber-400' : ''}
                    >
                      365 days
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setSettings({ ...settings, password_reset_days: 0 })}
                      className={settings.password_reset_days === 0 ? 'bg-amber-100 border-amber-400' : ''}
                    >
                      Never
                    </Button>
                  </div>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Notification Schedule:</strong> Users will see a warning banner 5 days before their password expires, 
                    with daily reminders. At 3 days remaining, the warning becomes more urgent.
                  </p>
                </div>
              </div>
              )}

              <div className="pt-4">
                <Button onClick={handleSavePasswordPolicy} disabled={savingPasswordPolicy} className="bg-slate-900 hover:bg-slate-800" data-testid="save-password-policy-btn">
                  {savingPasswordPolicy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Password Policy
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* SMTP Tab */}
        <TabsContent value="smtp" className="mt-6">
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="font-serif">SMTP Configuration</CardTitle>
              <CardDescription>Configure email server settings for notifications</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="smtp_host">SMTP Host</Label>
                  <Input
                    id="smtp_host"
                    value={settings.smtp_host}
                    onChange={(e) => setSettings({ ...settings, smtp_host: e.target.value })}
                    placeholder="smtp.gmail.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtp_port">SMTP Port</Label>
                  <Input
                    id="smtp_port"
                    type="number"
                    value={settings.smtp_port}
                    onChange={(e) => setSettings({ ...settings, smtp_port: parseInt(e.target.value) || 587 })}
                    placeholder="587"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtp_user">Username</Label>
                  <Input
                    id="smtp_user"
                    value={settings.smtp_user}
                    onChange={(e) => setSettings({ ...settings, smtp_user: e.target.value })}
                    placeholder="your-email@gmail.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtp_password">Password</Label>
                  <div className="relative">
                    <Input
                      id="smtp_password"
                      type={showSmtpPassword ? 'text' : 'password'}
                      value={settings.smtp_password}
                      onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                      placeholder="App password or SMTP password"
                      className="pr-10"
                      data-testid="smtp-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowSmtpPassword(v => !v)}
                      aria-label={showSmtpPassword ? 'Hide password' : 'Show password'}
                      title={showSmtpPassword ? 'Hide password' : 'Show password'}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-500 hover:text-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-300 rounded"
                      data-testid="smtp-password-toggle"
                    >
                      {showSmtpPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtp_from_email">From Email</Label>
                  <Input
                    id="smtp_from_email"
                    type="email"
                    value={settings.smtp_from_email}
                    onChange={(e) => setSettings({ ...settings, smtp_from_email: e.target.value })}
                    placeholder="noreply@yourcompany.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="smtp_from_name">From Name</Label>
                  <Input
                    id="smtp_from_name"
                    value={settings.smtp_from_name}
                    onChange={(e) => setSettings({ ...settings, smtp_from_name: e.target.value })}
                    placeholder="Asset Commander"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-2 pt-2">
                <Switch
                  id="smtp_use_tls"
                  checked={settings.smtp_use_tls}
                  onCheckedChange={(checked) => setSettings({ ...settings, smtp_use_tls: checked })}
                />
                <Label htmlFor="smtp_use_tls">Use TLS Encryption</Label>
              </div>
              <div className="pt-4 flex flex-wrap items-end gap-3">
                <Button onClick={handleSaveSMTP} disabled={saving} className="bg-slate-900 hover:bg-slate-800">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save SMTP Settings
                </Button>
                <div className="flex items-end gap-2">
                  <div className="space-y-1">
                    <Label htmlFor="test_email_address" className="text-xs text-slate-600">Test email address (optional)</Label>
                    <Input
                      id="test_email_address"
                      type="email"
                      value={testEmailAddress}
                      onChange={(e) => setTestEmailAddress(e.target.value)}
                      placeholder="you@example.com"
                      className="h-10 w-64"
                      data-testid="smtp-test-email-input"
                    />
                  </div>
                  <Button onClick={handleTestSMTP} variant="outline" disabled={saving} data-testid="smtp-test-email-btn">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                    Send Test Email
                  </Button>
                </div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> If you enter a <strong>test email address</strong> above, the email will be sent only to that address.
                  Leave it blank to send to every user who has their <strong>Email</strong> toggle enabled in User Management.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Communications Tab */}
        <TabsContent value="communications" className="mt-6">
          <div className="space-y-6">
            {/* Weekly Digest Settings */}
            <Card className="border-slate-200">
              <CardHeader
                className="cursor-pointer select-none"
                onClick={() => setDigestCollapsed(c => !c)}
                data-testid="weekly-digest-card-header"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <CardTitle className="font-serif flex items-center gap-2">
                      <Mail className="w-5 h-5" />
                      Weekly Task Digest
                    </CardTitle>
                    <CardDescription>
                      Send a weekly summary email to users with their assigned tasks
                    </CardDescription>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setDigestCollapsed(c => !c); }}
                    className="p-1 rounded hover:bg-slate-100 text-slate-500"
                    aria-label={digestCollapsed ? 'Expand Weekly Task Digest' : 'Collapse Weekly Task Digest'}
                    data-testid="weekly-digest-toggle"
                  >
                    {digestCollapsed ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
                  </button>
                </div>
              </CardHeader>
              {!digestCollapsed && (
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-700">Enable Weekly Digest</p>
                    <p className="text-sm text-slate-500">Automatically send digest emails on schedule</p>
                  </div>
                  <Switch
                    checked={digestSettings.digest_enabled}
                    onCheckedChange={(checked) => setDigestSettings(prev => ({ ...prev, digest_enabled: checked }))}
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="digest_day">Send On</Label>
                    <select
                      id="digest_day"
                      value={digestSettings.digest_day}
                      onChange={(e) => setDigestSettings(prev => ({ ...prev, digest_day: e.target.value }))}
                      className="w-full h-10 px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                    >
                      <option value="monday">Monday</option>
                      <option value="tuesday">Tuesday</option>
                      <option value="wednesday">Wednesday</option>
                      <option value="thursday">Thursday</option>
                      <option value="friday">Friday</option>
                      <option value="saturday">Saturday</option>
                      <option value="sunday">Sunday</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="digest_time">Send At</Label>
                    <Input
                      id="digest_time"
                      type="time"
                      value={digestSettings.digest_time}
                      onChange={(e) => setDigestSettings(prev => ({ ...prev, digest_time: e.target.value }))}
                    />
                  </div>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button onClick={handleSaveDigestSettings} disabled={savingDigest} className="bg-slate-900 hover:bg-slate-800">
                    {savingDigest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Settings
                  </Button>
                  <Button onClick={handlePreviewDigest} disabled={previewLoading} variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-50">
                    {previewLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                    Preview
                  </Button>
                  <Button onClick={handleSendDigestNow} disabled={sendingDigest} variant="outline" className="border-emerald-600 text-emerald-600 hover:bg-emerald-50">
                    {sendingDigest ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                    Send Now
                  </Button>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Digest emails are only sent to users who have <strong>both</strong> their 
                    <strong> Email</strong> and <strong>Tasks</strong> toggles enabled in User Management.
                  </p>
                </div>
              </CardContent>
              )}
            </Card>

            {/* Financial Statements (Quarterly) */}
            <Card className="border-slate-200" data-testid="financial-statements-card">
              <CardHeader
                className="cursor-pointer select-none"
                onClick={() => { const next = !fsCollapsed; setFsCollapsed(next); if (next === false) { fetchFsLLCs(); fetchFsInvestors(); } }}
                data-testid="financial-statements-card-header"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <CardTitle className="font-serif flex items-center gap-2">
                      <FileBarChart className="w-5 h-5" />
                      Financial Statements
                    </CardTitle>
                    <CardDescription>
                      Email a quarterly Income Statement summary to partners with an ownership interest in each LLC
                    </CardDescription>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); const next = !fsCollapsed; setFsCollapsed(next); if (next === false) { fetchFsLLCs(); fetchFsInvestors(); } }}
                    className="p-1 rounded hover:bg-slate-100 text-slate-500"
                    aria-label={fsCollapsed ? 'Expand Financial Statements' : 'Collapse Financial Statements'}
                    data-testid="financial-statements-toggle"
                  >
                    {fsCollapsed ? <ChevronDown className="w-5 h-5" /> : <ChevronUp className="w-5 h-5" />}
                  </button>
                </div>
              </CardHeader>
              {!fsCollapsed && (
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="fs_year">Year</Label>
                    <Input
                      id="fs_year"
                      type="number"
                      value={fsYear}
                      onChange={(e) => setFsYear(e.target.value)}
                      data-testid="fs-year-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fs_quarter">Quarter</Label>
                    <select
                      id="fs_quarter"
                      value={fsQuarter}
                      onChange={(e) => setFsQuarter(Number(e.target.value))}
                      className="w-full h-10 px-3 py-2 border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                      data-testid="fs-quarter-select"
                    >
                      <option value={1}>Q1 (Jan – Mar)</option>
                      <option value={2}>Q2 (Apr – Jun)</option>
                      <option value={3}>Q3 (Jul – Sep)</option>
                      <option value={4}>Q4 (Oct – Dec)</option>
                    </select>
                  </div>
                </div>

                {/* LLC Selector */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>LLCs</Label>
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                      <Checkbox
                        checked={fsAllLLCs}
                        onCheckedChange={(c) => setFsAllLLCs(!!c)}
                        data-testid="fs-all-llcs-checkbox"
                      />
                      All LLCs (auto-include any with activity)
                    </label>
                  </div>
                  {!fsAllLLCs && (
                    <div className="border border-slate-200 rounded-md p-3 max-h-48 overflow-y-auto">
                      {fsLLCs.length === 0 ? (
                        <p className="text-sm text-slate-500">Loading LLCs…</p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {fsLLCs.map(llc => (
                            <label key={llc.id} className="flex items-center gap-2 text-sm">
                              <Checkbox
                                checked={fsSelectedLLCs.includes(llc.id)}
                                onCheckedChange={() => toggleFsLLC(llc.id)}
                                data-testid={`fs-llc-${llc.id}`}
                              />
                              {llc.name}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Investor Selector */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label>Partners / Investors</Label>
                    <label className="flex items-center gap-2 text-sm text-slate-600">
                      <Checkbox
                        checked={fsAllInvestors}
                        onCheckedChange={(c) => setFsAllInvestors(!!c)}
                        data-testid="fs-all-investors-checkbox"
                      />
                      All eligible partners
                    </label>
                  </div>
                  {!fsAllInvestors && (
                    <div className="border border-slate-200 rounded-md p-3 max-h-48 overflow-y-auto">
                      {fsInvestors.length === 0 ? (
                        <p className="text-sm text-slate-500">Loading partners…</p>
                      ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {fsInvestors.map(inv => (
                            <label key={inv.id} className="flex items-center gap-2 text-sm">
                              <Checkbox
                                checked={fsSelectedInvestors.includes(inv.id)}
                                onCheckedChange={() => toggleFsInvestor(inv.id)}
                                data-testid={`fs-investor-${inv.id}`}
                              />
                              <span className="truncate">
                                {inv.name}
                                <span className="text-slate-400 text-xs ml-1">{inv.email}</span>
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-3 pt-2">
                  <Button
                    onClick={handlePreviewFinancialStatements}
                    disabled={fsLoadingPreview}
                    variant="outline"
                    className="border-blue-600 text-blue-600 hover:bg-blue-50"
                    data-testid="fs-preview-btn"
                  >
                    {fsLoadingPreview ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                    Preview Recipients
                  </Button>
                  <Button
                    onClick={handleSendFinancialStatements}
                    disabled={fsSending}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="fs-send-btn"
                  >
                    {fsSending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Send Quarterly Reports
                  </Button>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> Each partner receives a single email with summaries for every LLC they own.
                    Only LLCs with income or expense activity in the selected quarter are included.
                  </p>
                </div>
              </CardContent>
              )}
            </Card>

            {/* Financial Statements Preview Dialog */}
            <Dialog open={fsPreviewOpen} onOpenChange={setFsPreviewOpen}>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="fs-preview-dialog">
                <DialogHeader>
                  <DialogTitle>
                    Quarterly Statement Recipients
                    {fsPreview && (
                      <span className="text-sm font-normal text-slate-500 ml-2">
                        Q{fsPreview.quarter} {fsPreview.year} · {fsPreview.recipient_count} recipient(s)
                      </span>
                    )}
                  </DialogTitle>
                </DialogHeader>
                {renderFsPreviewBody(fsPreview)}
              </DialogContent>
            </Dialog>

            {/* Email Logs */}
            <Card className="border-slate-200">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-serif flex items-center gap-2">
                      <MessageSquare className="w-5 h-5" />
                      Email Communications Log
                    </CardTitle>
                    <CardDescription>Track all sent and failed email notifications</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={fetchEmailLogs} disabled={loadingLogs}>
                      <RefreshCw className={`w-4 h-4 mr-2 ${loadingLogs ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                    {emailLogs.length > 0 && (
                      <Button variant="outline" size="sm" onClick={clearEmailLogs} className="text-red-600 hover:text-red-700">
                        <Trash2 className="w-4 h-4 mr-2" />
                        Clear Logs
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {loadingLogs ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : emailLogs.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Mail className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                    <p className="text-lg font-medium">No email logs yet</p>
                    <p className="text-sm mt-1">Email communications will appear here once sent</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-800">
                          <TableHead className="text-white font-semibold">Status</TableHead>
                          <TableHead className="text-white font-semibold">Date/Time</TableHead>
                          <TableHead className="text-white font-semibold">Recipient</TableHead>
                          <TableHead className="text-white font-semibold">Subject</TableHead>
                          <TableHead className="text-white font-semibold">Type</TableHead>
                          <TableHead className="text-white font-semibold">Triggered By</TableHead>
                          <TableHead className="text-white font-semibold">Details</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {emailLogs.map((log, idx) => (
                          <TableRow key={log.id || idx} className="hover:bg-slate-50 cursor-pointer" onClick={() => log.id && openEmailPreview(log.id)} data-testid={`email-log-row-${idx}`}>
                            <TableCell>
                              {log.status === 'sent' ? (
                                <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Sent
                                </Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                                  <AlertCircle className="w-3 h-3 mr-1" />
                                  Failed
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-sm text-slate-600">
                              {new Date(log.created_at).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                            </TableCell>
                            <TableCell className="font-medium">{log.to_email}</TableCell>
                            <TableCell className="text-sm max-w-[200px] truncate">{log.subject}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="capitalize">{log.email_type || 'general'}</Badge>
                            </TableCell>
                            <TableCell className="text-sm text-slate-500">{log.triggered_by || '-'}</TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                {log.error ? (
                                  <span className="text-xs text-red-600 max-w-[150px] truncate block" title={log.error}>
                                    {log.error.length > 40 ? log.error.substring(0, 40) + '...' : log.error}
                                  </span>
                                ) : (
                                  <span className="text-xs text-slate-400">via {log.smtp_host}</span>
                                )}
                                <Eye className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
                
                {/* Summary stats */}
                {emailLogs.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-slate-200 flex gap-6">
                    <div className="text-sm">
                      <span className="text-slate-500">Total:</span>
                      <span className="ml-2 font-medium">{emailLogs.length}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-emerald-600">Sent:</span>
                      <span className="ml-2 font-medium">{emailLogs.filter(l => l.status === 'sent').length}</span>
                    </div>
                    <div className="text-sm">
                      <span className="text-red-600">Failed:</span>
                      <span className="ml-2 font-medium">{emailLogs.filter(l => l.status === 'failed').length}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Email Preview Dialog */}
        <Dialog open={!!emailPreview || loadingPreview} onOpenChange={() => { setEmailPreview(null); setLoadingPreview(false); }}>
          <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="font-serif flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Email Preview
              </DialogTitle>
            </DialogHeader>
            {loadingPreview ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : emailPreview ? (
              <div className="flex-1 overflow-hidden flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm bg-slate-50 rounded-lg p-3">
                  <div><span className="text-slate-500">To:</span> <span className="font-medium">{emailPreview.to_email}</span></div>
                  <div><span className="text-slate-500">From:</span> {emailPreview.from_name} &lt;{emailPreview.from_email}&gt;</div>
                  <div><span className="text-slate-500">Subject:</span> {emailPreview.subject}</div>
                  <div><span className="text-slate-500">Date:</span> {new Date(emailPreview.created_at).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}</div>
                  <div>
                    <span className="text-slate-500">Status:</span>{' '}
                    {emailPreview.status === 'sent'
                      ? <Badge className="bg-emerald-100 text-emerald-800 text-xs">Sent</Badge>
                      : <Badge className="bg-red-100 text-red-800 text-xs">Failed</Badge>}
                  </div>
                  <div><span className="text-slate-500">Type:</span> <Badge variant="outline" className="capitalize text-xs">{emailPreview.email_type}</Badge></div>
                </div>
                {emailPreview.error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-2 text-sm text-red-700">
                    <strong>Error:</strong> {emailPreview.error}
                  </div>
                )}
                {emailPreview.html_body ? (
                  <div className="flex-1 border border-slate-200 rounded-lg overflow-auto bg-white">
                    <iframe
                      title="Email Preview"
                      srcDoc={emailPreview.html_body}
                      className="w-full h-full min-h-[400px] border-0"
                      sandbox=""
                      data-testid="email-preview-iframe"
                    />
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <Mail className="w-10 h-10 mx-auto mb-3 text-slate-300" />
                    <p className="font-medium">No email content stored</p>
                    <p className="text-sm mt-1">Email content is only saved for new emails sent after this update</p>
                  </div>
                )}
              </div>
            ) : null}
          </DialogContent>
        </Dialog>

        {/* Backup/Restore Tab */}
        <TabsContent value="backup" className="mt-6">
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
              {/* Create Backup */}
              <Card className="border-slate-200 flex flex-col">
                <CardHeader>
                  <CardTitle className="font-serif flex items-center gap-2">
                    <Download className="w-5 h-5" />
                    Create Backup
                  </CardTitle>
                  <CardDescription>Download a complete backup of all your data</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 flex-1 flex flex-col">
                  <p className="text-sm text-slate-600">
                    This will export all LLCs, investors, transactions, ownerships, and custom categories to a JSON file.
                  </p>
                  <ul className="text-sm text-slate-500 space-y-1">
                    <li>• LLCs and their details</li>
                    <li>• Investor information</li>
                    <li>• All transactions</li>
                    <li>• Ownership records</li>
                    <li>• Custom categories</li>
                    <li>• Application settings</li>
                  </ul>
                  <Button onClick={handleCreateBackup} className="w-full bg-emerald-600 hover:bg-emerald-700 mt-auto">
                    <Download className="w-4 h-4 mr-2" />
                    Download Backup
                  </Button>
                </CardContent>
              </Card>

              {/* Restore Backup */}
              <Card className="border-slate-200 flex flex-col">
                <CardHeader>
                  <CardTitle className="font-serif flex items-center gap-2">
                    <Upload className="w-5 h-5" />
                    Restore Backup
                  </CardTitle>
                  <CardDescription>Restore data from a previously created backup file</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 flex-1 flex flex-col">
                  <div className="space-y-2">
                    <Label htmlFor="backup_file">Select Backup File</Label>
                    <Input
                      id="backup_file"
                      type="file"
                      accept=".json"
                      onChange={handleFileUpload}
                      className="cursor-pointer"
                    />
                  </div>
                  
                  {backupData && (
                    <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div className="flex items-center gap-2 text-emerald-800">
                        <CheckCircle className="w-4 h-4" />
                        <span className="font-medium">Backup file loaded</span>
                      </div>
                      <p className="text-sm text-emerald-700 mt-1">
                        Created: {new Date(backupData.created_at).toLocaleString('en-US', { timeZone: 'America/Chicago', month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                      </p>
                      <p className="text-sm text-emerald-700">
                        Version: {backupData.version}
                      </p>
                      <p className="text-sm text-emerald-700">
                        Records: {backupData.data?.llcs?.length || 0} LLCs, {backupData.data?.investors?.length || 0} investors, {backupData.data?.transactions?.length || 0} transactions
                      </p>
                    </div>
                  )}

                  <p className="text-xs text-rose-600 font-medium">
                    Warning: Restore will replace ALL existing data with the backup data. This action cannot be undone.
                  </p>

                  <Button 
                    onClick={handleRestore} 
                    disabled={!backupData || restoring}
                    className="w-full bg-amber-700 hover:bg-amber-800 mt-auto"
                  >
                    {restoring ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Restore Backup
                  </Button>
                </CardContent>
              </Card>
            </div>

            {/* Email Backup Schedule */}
            <Card className="border-slate-200" data-testid="backup-email-panel">
              <CardHeader>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <CardTitle className="font-serif flex items-center gap-2">
                      <Mail className="w-5 h-5" />
                      Email Backup Schedule
                    </CardTitle>
                    <CardDescription>Automatically email a backup to a specified address on a schedule</CardDescription>
                  </div>
                  <BackupStatusPill settings={backupEmailSettings} />
                </div>
                {backupEmailSettings.last_status === 'failed' && backupEmailSettings.last_error && (
                  <div className="mt-2 text-xs text-red-600">
                    Error: {backupEmailSettings.last_error}
                  </div>
                )}
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="backup-email-to">Send To</Label>
                    <Input
                      id="backup-email-to"
                      type="email"
                      placeholder="backup@example.com"
                      value={backupEmailSettings.email}
                      onChange={e => setBackupEmailSettings(s => ({ ...s, email: e.target.value }))}
                      data-testid="backup-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="backup-frequency">Frequency</Label>
                    <Select value={backupEmailSettings.frequency} onValueChange={v => setBackupEmailSettings(s => ({ ...s, frequency: v }))}>
                      <SelectTrigger id="backup-frequency" data-testid="backup-frequency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {backupEmailSettings.frequency === 'weekly' && (
                  <div className="space-y-2">
                    <Label htmlFor="backup-day-of-week">Day</Label>
                    <Select value={backupEmailSettings.day_of_week || 'monday'} onValueChange={v => setBackupEmailSettings(s => ({ ...s, day_of_week: v }))}>
                      <SelectTrigger id="backup-day-of-week" data-testid="backup-day-of-week-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monday">Monday</SelectItem>
                        <SelectItem value="tuesday">Tuesday</SelectItem>
                        <SelectItem value="wednesday">Wednesday</SelectItem>
                        <SelectItem value="thursday">Thursday</SelectItem>
                        <SelectItem value="friday">Friday</SelectItem>
                        <SelectItem value="saturday">Saturday</SelectItem>
                        <SelectItem value="sunday">Sunday</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  )}
                  {backupEmailSettings.frequency === 'monthly' && (
                  <div className="space-y-2">
                    <Label htmlFor="backup-day-of-month">Day of Month</Label>
                    <Input
                      id="backup-day-of-month"
                      type="number"
                      min={1}
                      max={28}
                      value={backupEmailSettings.day_of_month || 1}
                      onChange={e => {
                        const n = Math.max(1, Math.min(28, parseInt(e.target.value, 10) || 1));
                        setBackupEmailSettings(s => ({ ...s, day_of_month: n }));
                      }}
                      data-testid="backup-day-of-month-input"
                    />
                  </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="backup-time">Time (CST)</Label>
                    <Input
                      id="backup-time"
                      type="time"
                      value={backupEmailSettings.time}
                      onChange={e => setBackupEmailSettings(s => ({ ...s, time: e.target.value }))}
                      data-testid="backup-time-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>&nbsp;</Label>
                    <div className="flex items-center gap-3 h-10">
                      <Switch
                        id="backup-email-enabled"
                        checked={backupEmailSettings.enabled}
                        onCheckedChange={v => setBackupEmailSettings(s => ({ ...s, enabled: v }))}
                      />
                      <Label htmlFor="backup-email-enabled" className="cursor-pointer text-sm">
                        {backupEmailSettings.enabled ? 'Enabled' : 'Disabled'}
                      </Label>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3 pt-2">
                  <Button onClick={saveBackupEmailSettings} disabled={savingBackupEmail} className="bg-amber-700 hover:bg-amber-800" data-testid="save-backup-email-btn">
                    {savingBackupEmail ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                    Save Schedule
                  </Button>
                  <Button variant="outline" onClick={sendBackupNow} disabled={sendingBackupNow || !backupEmailSettings.email} data-testid="send-backup-now-btn">
                    {sendingBackupNow ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Send Backup Now
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Email Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0">
          <DialogHeader className="p-4 border-b bg-slate-50">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5" />
                Email Preview
                {previews.length > 0 && (
                  <span className="text-sm font-normal text-slate-500">
                    ({currentPreviewIndex + 1} of {previews.length})
                  </span>
                )}
              </DialogTitle>
              {previews.length > 1 && (
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPreviewIndex(prev => Math.max(0, prev - 1))}
                    disabled={currentPreviewIndex === 0}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPreviewIndex(prev => Math.min(previews.length - 1, prev + 1))}
                    disabled={currentPreviewIndex === previews.length - 1}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </DialogHeader>
          
          {previewLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : previews.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-500">
              <Mail className="w-12 h-12 mb-4 text-slate-300" />
              <p className="font-medium">No eligible users found</p>
              <p className="text-sm mt-1">Enable Email and Tasks for users to preview their digest</p>
            </div>
          ) : (
            <>
              {/* User info bar */}
              <div className="px-4 py-3 bg-slate-100 border-b flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-800">{previews[currentPreviewIndex]?.user_name}</p>
                  <p className="text-sm text-slate-500">{previews[currentPreviewIndex]?.user_email}</p>
                </div>
                <div className="flex gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <span className="text-slate-500">Tasks:</span>
                    <Badge variant="outline">{previews[currentPreviewIndex]?.task_count}</Badge>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="text-slate-500">Past Due:</span>
                    <Badge className={previews[currentPreviewIndex]?.past_due_count > 0 ? "bg-orange-100 text-orange-800" : "bg-slate-100 text-slate-800"}>
                      {previews[currentPreviewIndex]?.past_due_count}
                    </Badge>
                  </div>
                </div>
              </div>
              
              {/* Email content iframe */}
              <div className="overflow-auto" style={{ height: 'calc(90vh - 180px)' }}>
                <iframe
                  srcDoc={previews[currentPreviewIndex]?.html}
                  title="Email Preview"
                  className="w-full h-full border-0"
                  style={{ minHeight: '600px' }}
                />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Setup;
